/* Ornata Sample Service — photo-referenced geometry, identity-based animation.
   Box proportions from IMG_6916–6933; sample dimensions supplied by the user: 60 mm total, 40 mm glass, 20 mm sprayer, 3 mm headspace. */
window.createOrnataSampleBox = async function (stage, options = {}) {
  if (options.signal?.aborted)
    return { setItems() {}, setOpen() {}, resize() {}, dispose() {} };
  const fallback = stage.querySelector(".model-fallback");
  const interactive = options.interactive !== false;
  delete stage.dataset.inspectionState;
  delete stage.dataset.inspectionType;
  delete stage.dataset.inspectionRotation;
  stage.style.removeProperty("--inspection-focus");
  stage.dataset.modelState = "loading";
  stage.setAttribute("aria-busy", "true");
  try {
    const T = await import(options.assets.three);
    if (options.signal?.aborted)
      return { setItems() {}, setOpen() {}, resize() {}, dispose() {} };
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    const renderer = new T.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: "low-power",
    });
    renderer.setPixelRatio(
      Math.min(devicePixelRatio, options.pixelRatio || 1.8),
    );
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = T.PCFSoftShadowMap;
    renderer.toneMapping = T.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.domElement.setAttribute("aria-hidden", "true");
    renderer.domElement.className = "ornata-box-canvas";
    for (const child of [...stage.childNodes]) {
      if (child !== fallback) child.remove();
    }
    stage.prepend(renderer.domElement);
    const scene = new T.Scene(),
      camera = new T.OrthographicCamera(-6, 6, 5, -5, 0.1, 100);
    camera.position.set(-3.0, 10.5, 14);
    camera.lookAt(0, 2.0, -0.6);
    const mobileView = options.mobileOrientation
        ? matchMedia("(max-width: 800px)")
        : null,
      viewOrientation = () =>
        mobileView?.matches
          ? options.mobileOrientation
          : options.orientation || "right",
      frontAngle = Math.atan2(camera.position.x, camera.position.z + 0.6),
      orientationAngle = (view) =>
        view === "front"
          ? frontAngle
          : view === "left"
            ? 2 * frontAngle - 0.1
            : 0.1;
    let orientation = viewOrientation();
    stage.dataset.orientation = orientation;
    scene.add(new T.HemisphereLight(0xffffff, 0x888888, 2.4));
    const key = new T.DirectionalLight(0xffffff, 3.6);
    key.position.set(-6, 12, 9);
    key.castShadow = true;
    key.shadow.mapSize.set(
      options.shadowSize || 1024,
      options.shadowSize || 1024,
    );
    Object.assign(key.shadow.camera, {
      left: -10,
      right: 10,
      top: 12,
      bottom: -10,
      near: 0.1,
      far: 40,
    });
    key.shadow.bias = -0.0003;
    key.shadow.normalBias = 0.02;
    scene.add(key);
    const rim = new T.DirectionalLight(0xffffff, 2.0);
    rim.position.set(6, 6, -8);
    scene.add(rim);
    const env = new T.Scene();
    env.background = new T.Color("#b0b0b0");
    for (const [w, h, x, y, z, rx, ry] of [
      [10, 6, -6, 5, 5, 0, 0.7],
      [3, 12, 6, 4, 0, 0, -1.5],
      [8, 8, 0, 10, 0, Math.PI / 2, 0],
    ]) {
      const p = new T.Mesh(
        new T.PlaneGeometry(w, h),
        new T.MeshBasicMaterial({ color: 0xffffff, side: T.DoubleSide }),
      );
      p.position.set(x, y, z);
      p.rotation.set(rx, ry, 0);
      env.add(p);
    }
    const pmrem = new T.PMREMGenerator(renderer),
      envTarget = pmrem.fromScene(env, 0.02);
    scene.environment = envTarget.texture;
    pmrem.dispose();
    env.traverse((mesh) => {
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    });
    const noise = document.createElement("canvas");
    noise.width = noise.height = 256;
    const ctx = noise.getContext("2d"),
      pix = ctx.createImageData(256, 256);
    let seed = 3;
    for (let i = 0; i < pix.data.length; i += 4) {
      seed = (seed * 16807) % 2147483647;
      const c = 100 + (seed % 90);
      pix.data.set([c, c, c, 255], i);
    }
    ctx.putImageData(pix, 0, 0);
    const bump = new T.CanvasTexture(noise);
    bump.wrapS = bump.wrapT = T.RepeatWrapping;
    bump.repeat.set(5, 5);
    const paper = new T.MeshStandardMaterial({
      color: "#141414",
      roughness: 0.87,
      envMapIntensity: 0.16,
      bumpMap: bump,
      bumpScale: 0.012,
    });
    const lining = new T.MeshStandardMaterial({
      color: "#131313",
      roughness: 0.98,
      envMapIntensity: 0.08,
      bumpMap: bump,
      bumpScale: 0.006,
    });
    const foam = new T.MeshStandardMaterial({
      color: "#080808",
      roughness: 1,
      envMapIntensity: 0.04,
      bumpMap: bump,
      bumpScale: 0.045,
    });
    const glass = new T.MeshPhysicalMaterial({
      color: "#f4f2e8",
      roughness: 0.08,
      metalness: 0,
      transparent: true,
      opacity: 0.14,
      depthWrite: false,
      envMapIntensity: 1.1,
      clearcoat: 1,
      clearcoatRoughness: 0.07,
      side: T.DoubleSide,
    });
    const liquid = new T.MeshPhysicalMaterial({
      color: "#d3aa58",
      roughness: 0.15,
      metalness: 0,
      transparent: true,
      opacity: 0.74,
      depthWrite: false,
      envMapIntensity: 0.5,
    });
    const capMat = new T.MeshStandardMaterial({
      color: "#060606",
      roughness: 0.23,
      metalness: 0.25,
    });
    const tubeMat = new T.MeshStandardMaterial({
      color: "#e8e4d6",
      roughness: 0.4,
      transparent: true,
      opacity: 0.6,
    });
    const pivot = new T.Group();
    scene.add(pivot);
    pivot.position.set(0, 2, -0.5);
    const box = new T.Group();
    pivot.add(box);
    box.position.set(0, -2, 0.5);
    pivot.rotation.y = orientationAngle(orientation);
    function block(w, h, d, x, y, z, mat, parent = box) {
      const m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      m.castShadow = m.receiveShadow = true;
      parent.add(m);
      return m;
    }
    block(7.26, 0.1, 5.1, 0, 0.1, 0, paper);
    block(7.08, 0.18, 4.94, 0, 0.28, 0, lining);
    block(7.18, 0.67, 0.095, 0, 0.48, 2.46, paper);
    block(7.18, 0.67, 0.095, 0, 0.48, -2.46, paper);
    block(0.095, 0.67, 4.9, -3.54, 0.48, 0, paper);
    block(0.095, 0.67, 4.9, 3.54, 0.48, 0, paper);
    // Foam insert: five straight vial cavities, with the real circular finger recesses.
    const shape = new T.Shape();
    shape.moveTo(-3.44, -2.36);
    shape.lineTo(3.44, -2.36);
    shape.lineTo(3.44, 2.36);
    shape.lineTo(-3.44, 2.36);
    shape.closePath();
    const xs = [-2.54, -1.27, 0, 1.27, 2.54],
      slotZ = -0.48;
    xs.forEach((x) => {
      const p = new T.Path(),
        w = 0.218,
        c = 0.48,
        r = 0.34;
      p.moveTo(x - w, -0.7);
      p.lineTo(x + w, -0.7);
      p.lineTo(x + w, c - r);
      p.bezierCurveTo(x + 0.58, c - r, x + 0.58, c + r, x + w, c + r);
      p.lineTo(x + w, 1.66);
      p.lineTo(x - w, 1.66);
      p.lineTo(x - w, c + r);
      p.bezierCurveTo(x - 0.58, c + r, x - 0.58, c - r, x - w, c - r);
      p.closePath();
      shape.holes.push(p);
    });
    const stripHole = new T.Path();
    stripHole.moveTo(-3.08, -1.96);
    stripHole.lineTo(3.08, -1.96);
    stripHole.lineTo(3.08, -1.34);
    stripHole.lineTo(-3.08, -1.34);
    stripHole.closePath();
    shape.holes.push(stripHole);
    const insert = new T.Mesh(
      new T.ExtrudeGeometry(shape, {
        depth: 0.31,
        bevelEnabled: false,
        curveSegments: 20,
      }),
      foam,
    );
    insert.rotation.x = -Math.PI / 2;
    insert.position.y = 0.4;
    insert.castShadow = insert.receiveShadow = true;
    box.add(insert);
    // Blotter strips — stacked, with a broad embossed end and tapered tip.
    const stripShape = new T.Shape();
    stripShape.moveTo(-2.98, -0.23);
    stripShape.quadraticCurveTo(-3.04, -0.23, -3.04, -0.16);
    stripShape.lineTo(-3.04, 0.16);
    stripShape.quadraticCurveTo(-3.04, 0.23, -2.98, 0.23);
    stripShape.lineTo(0.6, 0.23);
    stripShape.lineTo(2.98, 0.055);
    stripShape.quadraticCurveTo(3.03, 0, 2.98, -0.055);
    stripShape.lineTo(0.6, -0.23);
    stripShape.closePath();
    const blotterMat = new T.MeshStandardMaterial({
      color: "#e8e2d6",
      roughness: 1,
      bumpMap: bump,
      bumpScale: 0.009,
    });
    const strips = new T.Group();
    strips.userData.action = "strips";
    box.add(strips);
    for (let i = 0; i < 5; i++) {
      const group = new T.Group();
      group.position.set(
        (i - 2) * 0.012,
        0.62 + i * 0.025,
        1.65 + (i - 2) * 0.022,
      );
      strips.add(group);
      const m = new T.Mesh(
        new T.ExtrudeGeometry(stripShape, {
          depth: 0.018,
          bevelEnabled: false,
        }),
        blotterMat,
      );
      m.rotation.x = -Math.PI / 2;
      m.receiveShadow = true;
      m.castShadow = true;
      group.add(m);
      const emboss = new T.Mesh(
        new T.RingGeometry(0.125, 0.135, 40),
        new T.MeshBasicMaterial({ color: "#c4bbad" }),
      );
      emboss.rotation.x = -Math.PI / 2;
      emboss.position.set(-2.67, 0.02, 0);
      group.add(emboss);
    }
    // Book-style magnetic lid. Its inside is intentionally plain, as photographed.
    const lid = new T.Group();
    lid.position.set(0, 0.86, -2.52);
    box.add(lid);
    lid.userData.action = "lid";
    block(7.26, 0.12, 5.12, 0, 0, 2.53, paper, lid);
    block(7.1, 0.012, 4.96, 0, -0.067, 2.53, lining, lid);
    const flap = new T.Group();
    flap.position.set(0, -0.04, 5.04);
    lid.add(flap);
    block(7.26, 0.64, 0.095, 0, -0.31, 0, paper, flap);
    // Original lid print from the supplied top photo, mapped to the four lid corners.
    const printGeo = new T.PlaneGeometry(7.2, 5.04),
      uv = printGeo.attributes.uv;
    [
      [132 / 1200, 1 - 460 / 1600],
      [1100 / 1200, 1 - 450 / 1600],
      [128 / 1200, 1 - 1150 / 1600],
      [1130 / 1200, 1 - 1130 / 1600],
    ].forEach(([u, v], i) => uv.setXY(i, u, v));
    const printMat = new T.MeshStandardMaterial({
      color: "#ffffff",
      roughness: 0.88,
      envMapIntensity: 0.08,
    });
    const print = new T.Mesh(printGeo, printMat);
    print.rotation.x = -Math.PI / 2;
    print.position.set(0, 0.063, 2.53);
    lid.add(print);
    let texturesLoaded = false,
      textureFailed = false;
    const textureManager = new T.LoadingManager();
    textureManager.onLoad = () => {
      if (disposed) return;
      texturesLoaded = true;
      wake();
    };
    textureManager.onError = () => {
      if (disposed) return;
      textureFailed = true;
      stage.dataset.modelState = "fallback";
      stage.setAttribute("aria-busy", "false");
    };
    const loader = new T.TextureLoader(textureManager);
    loader.load(options.assets.lid, (t) => {
      if (disposed) {
        t.dispose();
        return;
      }
      t.colorSpace = T.SRGBColorSpace;
      t.anisotropy = 4;
      printMat.map = t;
      printMat.needsUpdate = true;
      wake();
    });

    const logoTexture = loader.load(options.assets.logo, (t) => {
      if (disposed) {
        t.dispose();
        return;
      }
      t.colorSpace = T.SRGBColorSpace;
      wake();
    });
    function cylinder(r, h, y, mat, parent) {
      const m = new T.Mesh(new T.CylinderGeometry(r, r, h, 32), mat);
      m.position.y = y;
      m.castShadow = true;
      parent.add(m);
      return m;
    }
    function makeSample(id, index) {
      const outer = new T.Group();
      box.add(outer);
      const vial = new T.Group();
      vial.rotation.x = -Math.PI / 2;
      outer.add(vial);
      // 0.036 scene units per millimetre. Exact longitudinal limits: -1.08 to +1.08.
      // Glass: 40 mm (-1.08 to .36); complete sprayer: 20 mm (.36 to 1.08).
      cylinder(0.18, 1.4, -0.38, glass, vial);
      cylinder(0.145, 0.04, 0.34, glass, vial);
      cylinder(0.174, 0.04, -1.06, glass, vial);
      // Liquid surface .108 units (3 mm) below the glass top. Approx. 2 ml inside.
      cylinder(0.152, 1.292, -0.394, liquid, vial);
      cylinder(
        0.152,
        0.008,
        0.248,
        new T.MeshStandardMaterial({
          color: "#d4b87d",
          roughness: 0.12,
          transparent: true,
          opacity: 0.7,
        }),
        vial,
      );
      cylinder(0.181, 0.2, 0.46, capMat, vial);
      cylinder(0.133, 0.11, 0.615, capMat, vial);
      cylinder(0.184, 0.41, 0.875, capMat, vial);
      const tube = cylinder(0.012, 1.45, -0.305, tubeMat, vial);
      tube.position.x = 0.035;
      const nozzle = new T.Mesh(
        new T.CircleGeometry(0.029, 20),
        new T.MeshStandardMaterial({
          color: "#b8b8b8",
          roughness: 0.35,
          metalness: 0.5,
        }),
      );
      nozzle.position.set(0, 0.96, 0.185);
      vial.add(nozzle);
      const hole = new T.Mesh(
        new T.CircleGeometry(0.012, 16),
        new T.MeshBasicMaterial({ color: "#080808" }),
      );
      hole.position.set(0, 0.96, 0.187);
      vial.add(hole);
      const gleam = new T.Mesh(
        new T.CylinderGeometry(0.181, 0.181, 1.32, 48, 1, true, 0.35, 0.065),
        new T.MeshBasicMaterial({
          color: "#ffffff",
          transparent: true,
          opacity: 0.38,
          depthWrite: false,
        }),
      );
      gleam.position.y = -0.38;
      vial.add(gleam);
      // The print follows the glass cylinder; UVs turn the original horizontal logo upright.
      const labelGeometry = new T.CylinderGeometry(
          0.182,
          0.182,
          1.04,
          48,
          1,
          true,
          -0.85,
          1.7,
        ),
        labelUV = labelGeometry.attributes.uv;
      for (let i = 0; i < labelUV.count; i++) {
        const u = labelUV.getX(i),
          v = labelUV.getY(i);
        labelUV.setXY(i, v, 1 - u);
      }
      const label = new T.Mesh(
        labelGeometry,
        new T.MeshBasicMaterial({
          map: logoTexture,
          transparent: true,
          depthWrite: false,
          opacity: 0.88,
          side: T.FrontSide,
        }),
      );
      label.position.y = -0.38;
      vial.add(label);
      outer.position.set(xs[index], 3.5, slotZ);
      outer.visible = true;
      outer.userData.action = "sample";
      const materials = new Set();
      outer.traverse((mesh) => {
        if (
          mesh.material &&
          ![glass, liquid, capMat, tubeMat].includes(mesh.material)
        )
          materials.add(mesh.material);
      });
      return {
        id,
        outer,
        materials,
        index,
        target: true,
        settled: false,
        delay: 0,
      };
    }
    const angle = -Math.PI * 0.57,
      baseY = 0.7;
    let desiredOpen = true,
      inViewport = true,
      active = !document.hidden,
      disposed = false,
      raf = 0,
      last = 0,
      itemsKey = "",
      drag = null;
    const samples = new Map(),
      homeQuaternion = pivot.quaternion.clone(),
      rotationTarget = homeQuaternion.clone(),
      abort = new AbortController(),
      events = { signal: abort.signal };
    lid.rotation.x = angle;
    let inspection = null,
      detailRenderer = null;
    const detailScene = new T.Scene();
    detailScene.environment = envTarget.texture;
    detailScene.add(new T.HemisphereLight(0xffffff, 0x888888, 2.4));
    for (const light of [key, rim]) {
      const copy = light.clone();
      copy.castShadow = false;
      detailScene.add(copy);
    }
    const returnButton = document.createElement("button");
    returnButton.type = "button";
    returnButton.className = "model-return";
    returnButton.setAttribute("aria-label", "Terug naar de set");
    returnButton.title = "Terug naar de set";
    returnButton.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="m9 5-5 5 5 5M4 10h9a6 6 0 0 1 0 12" transform="translate(0 -2)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    returnButton.hidden = true;
    returnButton.addEventListener("click", () => closeInspection(), events);
    const detailCaption = document.createElement("span");
    detailCaption.className = "model-inspection-caption";
    detailCaption.hidden = true;
    detailCaption.setAttribute("aria-live", "polite");
    if (interactive) stage.append(returnButton, detailCaption);
    const magnifier =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5"/><path d="m15 15 4.5 4.5"/></svg>';
    const lidButton = document.createElement("button");
    lidButton.className = "lid-hotspot";
    lidButton.type = "button";
    lidButton.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="m7 10 5 5 5-5"/></svg>';
    const sampleButton = document.createElement("button"),
      stripButton = document.createElement("button");
    for (const [button, type, label] of [
      [sampleButton, "sample", "Sample van dichtbij bekijken"],
      [stripButton, "strips", "Geurstrips van dichtbij bekijken"],
    ]) {
      button.className = "inspect-hotspot";
      button.type = "button";
      button.dataset.inspect = type;
      button.innerHTML = magnifier;
      button.setAttribute("aria-label", label);
      button.title = label;
      button.addEventListener("click", () => inspect(type), events);
    }
    if (interactive) stage.append(lidButton, sampleButton, stripButton);
    stage.setAttribute("role", interactive ? "group" : "img");
    if (interactive) stage.tabIndex = 0;
    stage.dataset.stripCount = "5";
    stage.dataset.sampleDimensions = "60/40/20 mm";
    stage.dataset.headspace = "3 mm";
    function inspectionGoal() {
      const width = camera.right - camera.left,
        height = camera.top - camera.bottom;
      const position = new T.Vector3(
        (camera.left + camera.right) / 2,
        (camera.top + camera.bottom) / 2 + height * 0.03,
        -6,
      ).applyMatrix4(camera.matrixWorld);
      const sample = inspection.type === "sample";
      return {
        position,
        scale: Math.min(
          (width * 0.68) / (sample ? 0.55 : 6.2),
          (height * 0.59) / (sample ? 2.16 : 2.7),
        ),
      };
    }
    function inspect(type, item, requestedObject) {
      if (inspection || !interactive || !desiredOpen) return;
      const sample =
        [...samples.values()]
          .filter((s) => s.target && s.settled)
          .find((s) => s.id === String(item?.id)) ||
        [...samples.values()].filter((s) => s.target && s.settled).at(-1);
      const object =
        requestedObject || (type === "sample" ? sample?.outer : strips);
      if (!object) return;
      try {
        if (!detailRenderer) {
          detailRenderer = new T.WebGLRenderer({
            alpha: true,
            antialias: true,
            powerPreference: "low-power",
          });
          detailRenderer.setPixelRatio(
            Math.min(devicePixelRatio, options.pixelRatio || 1.8),
          );
          detailRenderer.toneMapping = renderer.toneMapping;
          detailRenderer.toneMappingExposure = renderer.toneMappingExposure;
          detailRenderer.domElement.className = "ornata-inspection-canvas";
          detailRenderer.domElement.setAttribute("aria-hidden", "true");
          stage.append(detailRenderer.domElement);
        }
        detailRenderer.setSize(stage.clientWidth, stage.clientHeight, false);
      } catch (_) {
        detailRenderer?.dispose();
        detailRenderer = null;
        return;
      }
      rotationTarget.copy(pivot.quaternion);
      scene.updateMatrixWorld(true);
      const center = new T.Box3()
        .setFromObject(object)
        .getCenter(new T.Vector3());
      const quaternion = object.getWorldQuaternion(new T.Quaternion());
      const group = new T.Group();
      group.position.copy(center);
      group.quaternion.copy(quaternion);
      detailScene.add(group);
      group.updateMatrixWorld(true);
      inspection = {
        type,
        object,
        group,
        parent: object.parent,
        localPosition: object.position.clone(),
        localQuaternion: object.quaternion.clone(),
        localScale: object.scale.clone(),
        homePosition: center.clone(),
        homeQuaternion: quaternion.clone(),
        fromPosition: center.clone(),
        fromQuaternion: quaternion.clone(),
        fromScale: 1,
        rotationTarget: camera.quaternion
          .clone()
          .multiply(
            new T.Quaternion().setFromAxisAngle(
              new T.Vector3(1, 0, 0),
              Math.PI / 2,
            ),
          ),
        phase: "out",
        elapsed: 0,
        duration: reduced.matches ? 0 : 0.85,
        amount: 0,
        origin: document.activeElement,
        ariaLabel: stage.getAttribute("aria-label"),
        stripPoses:
          type === "strips"
            ? strips.children.map((child) => ({
                child,
                position: child.position.clone(),
                quaternion: child.quaternion.clone(),
              }))
            : [],
      };
      inspection.upright = inspection.rotationTarget.clone();
      group.attach(object);
      const goal = inspectionGoal();
      inspection.toPosition = goal.position;
      inspection.toScale = goal.scale;
      inspection.toQuaternion = inspection.upright.clone();
      inspection.control = center
        .clone()
        .lerp(goal.position, 0.45)
        .add(
          new T.Vector3(
            0,
            (camera.top - camera.bottom) * 0.22,
            0,
          ).applyQuaternion(camera.quaternion),
        );
      stage.dataset.inspectionState = "out";
      stage.dataset.inspectionType = type;
      detailRenderer.domElement.hidden = false;
      returnButton.hidden = false;
      detailCaption.hidden = false;
      const title = document.createElement("strong"),
        hint = document.createElement("span");
      title.textContent =
        type === "sample"
          ? item?.name || sample?.item?.name || "2 ml sample"
          : "Vijf geurstrips";
      hint.textContent = "Sleep om 360° te bekijken";
      detailCaption.replaceChildren(title, hint);
      stage.setAttribute(
        "aria-label",
        title.textContent +
          ". Sleep of gebruik de pijltjestoetsen om te draaien. Home herstelt de weergave. Escape gaat terug naar de set.",
      );
      stage.focus({ preventScroll: true });
      drag = null;
      wake();
    }
    function finishInspection(restoreFocus = true) {
      if (!inspection) return;
      const current = inspection;
      current.parent.add(current.object);
      current.object.position.copy(current.localPosition);
      current.object.quaternion.copy(current.localQuaternion);
      current.object.scale.copy(current.localScale);
      for (const pose of current.stripPoses) {
        pose.child.position.copy(pose.position);
        pose.child.quaternion.copy(pose.quaternion);
      }
      detailScene.remove(current.group);
      inspection = null;
      drag = null;
      delete stage.dataset.inspectionState;
      delete stage.dataset.inspectionType;
      delete stage.dataset.inspectionRotation;
      stage.style.removeProperty("--inspection-focus");
      stage.setAttribute("aria-label", current.ariaLabel || "Ornata samplebox");
      if (detailRenderer) detailRenderer.domElement.hidden = true;
      returnButton.hidden = true;
      detailCaption.hidden = true;
      overlays();
      if (restoreFocus && current.origin?.isConnected)
        current.origin.focus({ preventScroll: true });
    }
    function closeInspection(instant = false, restoreFocus = true) {
      if (!inspection) return;
      if (instant || reduced.matches) {
        finishInspection(restoreFocus);
        wake();
        return;
      }
      if (inspection.phase === "in") return;
      inspection.phase = "in";
      inspection.elapsed = 0;
      inspection.duration = 0.65;
      inspection.fromPosition = inspection.group.position.clone();
      inspection.fromQuaternion = inspection.group.quaternion.clone();
      inspection.fromScale = inspection.group.scale.x;
      inspection.fromAmount = inspection.amount;
      inspection.toPosition = inspection.homePosition.clone();
      inspection.toQuaternion = inspection.homeQuaternion.clone();
      inspection.toScale = 1;
      inspection.restoreFocus = restoreFocus;
      inspection.control = inspection.fromPosition
        .clone()
        .lerp(inspection.toPosition, 0.55)
        .add(
          new T.Vector3(
            0,
            (camera.top - camera.bottom) * 0.18,
            0,
          ).applyQuaternion(camera.quaternion),
        );
      stage.dataset.inspectionState = "in";
      drag = null;
      wake();
    }
    function moveInspection(dt) {
      if (!inspection) return false;
      const current = inspection;
      let moving = false;
      if (current.phase !== "ready") {
        current.elapsed += dt;
        const progress = current.duration
          ? Math.min(1, current.elapsed / current.duration)
          : 1;
        const ease = progress * progress * (3 - 2 * progress),
          inverse = 1 - ease;
        current.group.position
          .copy(current.fromPosition)
          .multiplyScalar(inverse * inverse)
          .addScaledVector(current.control, 2 * inverse * ease)
          .addScaledVector(current.toPosition, ease * ease);
        current.group.quaternion.slerpQuaternions(
          current.fromQuaternion,
          current.toQuaternion,
          ease,
        );
        current.group.scale.setScalar(
          T.MathUtils.lerp(current.fromScale, current.toScale, ease),
        );
        current.amount =
          current.phase === "out" ? ease : current.fromAmount * (1 - ease);
        for (let i = 0; i < current.stripPoses.length; i++) {
          const pose = current.stripPoses[i];
          pose.child.position
            .copy(pose.position)
            .add(new T.Vector3(0, 0, (i - 2) * 0.5 * current.amount));
          pose.child.quaternion
            .copy(pose.quaternion)
            .multiply(
              new T.Quaternion().setFromAxisAngle(
                new T.Vector3(0, 1, 0),
                (i - 2) * 0.08 * current.amount,
              ),
            );
        }
        if (progress === 1) {
          if (current.phase === "in") {
            finishInspection(current.restoreFocus);
            return false;
          }
          current.phase = "ready";
          stage.dataset.inspectionState = "ready";
        } else moving = true;
      } else {
        current.group.quaternion.slerp(
          current.rotationTarget,
          reduced.matches ? 1 : 1 - Math.exp(-18 * dt),
        );
        moving =
          current.group.quaternion.angleTo(current.rotationTarget) > 0.0005;
        if (!moving) current.group.quaternion.copy(current.rotationTarget);
      }
      stage.style.setProperty("--inspection-focus", current.amount.toFixed(3));
      stage.dataset.inspectionRotation = current.group.quaternion
        .toArray()
        .map((n) => n.toFixed(3))
        .join(",");
      return moving;
    }
    function toggle() {
      closeInspection(true, false);
      desiredOpen = !desiredOpen;
      wake();
      return desiredOpen;
    }
    lidButton.addEventListener("click", toggle, events);
    function place(button, point) {
      const p = point.project(camera),
        w = stage.clientWidth,
        h = stage.clientHeight;
      button.style.left =
        Math.max(18, Math.min(w - 18, ((p.x + 1) * w) / 2)) + "px";
      button.style.top =
        Math.max(18, Math.min(h - 18, ((1 - p.y) * h) / 2)) + "px";
    }
    function overlays() {
      stage.dataset.lidOpen = String(desiredOpen);
      if (!interactive) return;
      lidButton.hidden = !!inspection;
      if (inspection) {
        sampleButton.hidden = true;
        stripButton.hidden = true;
        return;
      }
      scene.updateMatrixWorld(true);
      place(lidButton, lid.localToWorld(new T.Vector3(2.82, -0.09, 4.3)));
      const label = desiredOpen ? "Deksel sluiten" : "Deksel openen";
      lidButton.setAttribute("aria-label", label);
      lidButton.title = label;
      lidButton.dataset.open = String(desiredOpen);
      stage.dataset.lidOpen = String(desiredOpen);
      const normal = new T.Vector3(0, 1, 0).transformDirection(box.matrixWorld),
        towardCamera = camera.position
          .clone()
          .sub(box.localToWorld(new T.Vector3(0, baseY, 0))),
        visible = lid.rotation.x < -0.95 && normal.dot(towardCamera) > 0;
      const sample = [...samples.values()]
        .filter((s) => s.target && s.settled)
        .at(-1);
      sampleButton.hidden = !visible || !sample;
      stripButton.hidden = !visible;
      if (sample)
        place(
          sampleButton,
          box.localToWorld(
            new T.Vector3(xs[sample.index] + 0.36, baseY + 0.1, slotZ + 0.3),
          ),
        );
      place(stripButton, box.localToWorld(new T.Vector3(2.55, 0.78, 1.65)));
    }
    function draw() {
      if (
        !disposed &&
        texturesLoaded &&
        !textureFailed &&
        active &&
        stage.clientWidth &&
        stage.clientHeight
      ) {
        renderer.render(scene, camera);
        if (inspection && detailRenderer)
          detailRenderer.render(detailScene, camera);
        overlays();
        if (
          texturesLoaded &&
          !textureFailed &&
          stage.dataset.modelState !== "ready"
        ) {
          stage.dataset.modelState = "ready";
          stage.setAttribute("aria-busy", "false");
          stage.dispatchEvent(new CustomEvent("ornata:model-ready"));
        }
      }
    }
    function homePose(s) {
      box.updateWorldMatrix(true, false);
      return {
        position: box.localToWorld(new T.Vector3(xs[s.index], baseY, slotZ)),
        quaternion: box.getWorldQuaternion(new T.Quaternion()),
      };
    }
    function airPosition(home) {
      const c = home.clone().applyMatrix4(camera.matrixWorldInverse),
        up = new T.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
      return home.clone().addScaledVector(up, camera.top - c.y + 3.2);
    }
    function flight(s, enter, newSample = false) {
      scene.updateMatrixWorld(true);
      const home = homePose(s);
      scene.attach(s.outer);
      if (newSample) {
        s.outer.position.copy(airPosition(home.position));
        s.outer.quaternion.copy(home.quaternion);
      }
      s.from = s.outer.position.clone();
      s.fromQuaternion = s.outer.quaternion.clone();
      s.elapsed = 0;
      s.duration = enter ? 0.86 : 0.68;
      s.target = enter;
      s.settled = false;
    }
    function settle(s) {
      box.attach(s.outer);
      s.outer.position.set(xs[s.index], baseY, slotZ);
      s.outer.quaternion.identity();
      s.settled = true;
    }
    function remove(s) {
      s.outer.removeFromParent();
      s.outer.traverse((m) => m.geometry?.dispose());
      // These materials belong to this vial; its logo texture is shared.
      s.materials.forEach((material) => material.dispose());
      samples.delete(s.id);
    }
    function publishState() {
      stage.dataset.renderedIds = [...samples.values()]
        .filter((s) => s.target && s.settled)
        .sort((a, b) => a.index - b.index)
        .map((s) => s.id)
        .join(",");
      stage.dataset.moving = String(
        [...samples.values()].some((s) => !s.settled) ||
          Math.abs(lid.rotation.x - (desiredOpen ? angle : 0)) > 0.005,
      );
      stage.dataset.rotation = pivot.quaternion
        .toArray()
        .map((n) => n.toFixed(3))
        .join(",");
    }
    function tick(now) {
      raf = 0;
      if (disposed) return;
      const dt = Math.min((now - (last || now - 16)) / 1000, 0.05);
      last = now;
      let moving = false;
      const pending = [...samples.values()].some((s) => !s.settled),
        lidTarget = desiredOpen || pending ? angle : 0;
      {
        lid.rotation.x = reduced.matches
          ? lidTarget
          : T.MathUtils.damp(lid.rotation.x, lidTarget, 8, dt);
        if (Math.abs(lid.rotation.x - lidTarget) > 0.001) moving = true;
        else lid.rotation.x = lidTarget;
      }
      pivot.quaternion.slerp(
        rotationTarget,
        reduced.matches ? 1 : 1 - Math.exp(-18 * dt),
      );
      if (pivot.quaternion.angleTo(rotationTarget) > 0.0005) moving = true;
      else pivot.quaternion.copy(rotationTarget);
      scene.updateMatrixWorld(true);
      for (const s of samples.values()) {
        if (s.settled) continue;
        moving = true;
        if (lid.rotation.x > -0.95) continue;
        s.elapsed += dt;
        const t = Math.min(1, s.elapsed / s.duration),
          ease = s.target
            ? t < 0.5
              ? 4 * t * t * t
              : 1 - Math.pow(-2 * t + 2, 3) / 2
            : t * t * t,
          home = homePose(s);
        s.outer.position.lerpVectors(
          s.from,
          s.target ? home.position : airPosition(home.position),
          ease,
        );
        s.outer.quaternion.slerpQuaternions(
          s.fromQuaternion,
          home.quaternion,
          ease,
        );
        if (t === 1) {
          if (s.target) settle(s);
          else remove(s);
        }
      }
      if (!desiredOpen && Math.abs(lid.rotation.x) > 0.001) moving = true;
      moving = moveInspection(dt) || moving;
      publishState();
      draw();
      if (moving && active) raf = requestAnimationFrame(tick);
      else last = 0;
    }
    function wake() {
      if (!disposed && texturesLoaded && !textureFailed && !raf && active)
        raf = requestAnimationFrame(tick);
    }
    scene.updateMatrixWorld(true);
    camera.updateMatrixWorld(true);
    // Frame each starting view before adding samples, so selections and flights
    // never change the box's size. Other sample models keep the existing view.
    const orientationBounds = new Map();
    for (const view of new Set(
      [options.orientation || "right", options.mobileOrientation].filter(
        Boolean,
      ),
    )) {
      pivot.rotation.y = orientationAngle(view);
      scene.updateMatrixWorld(true);
      const bounds = new T.Box3();
      box.traverse((m) => {
        if (!m.geometry) return;
        m.geometry.computeBoundingBox();
        const b = m.geometry.boundingBox;
        for (const x of [b.min.x, b.max.x])
          for (const y of [b.min.y, b.max.y])
            for (const z of [b.min.z, b.max.z])
              bounds.expandByPoint(
                new T.Vector3(x, y, z)
                  .applyMatrix4(m.matrixWorld)
                  .applyMatrix4(camera.matrixWorldInverse),
              );
      });
      orientationBounds.set(view, bounds);
    }
    pivot.quaternion.copy(homeQuaternion);
    scene.updateMatrixWorld(true);
    let renderedWidth = 0,
      renderedHeight = 0;
    function resize() {
      const nextOrientation = viewOrientation();
      if (!disposed && nextOrientation !== orientation) {
        closeInspection(true, false);
        orientation = nextOrientation;
        homeQuaternion.setFromAxisAngle(
          new T.Vector3(0, 1, 0),
          orientationAngle(orientation),
        );
        pivot.quaternion.copy(homeQuaternion);
        rotationTarget.copy(homeQuaternion);
        drag = null;
        stage.dataset.orientation = orientation;
        renderedWidth = renderedHeight = 0;
      }
      const w = stage.clientWidth,
        h = stage.clientHeight;
      if (disposed || !w || !h || (w === renderedWidth && h === renderedHeight))
        return;
      renderedWidth = w;
      renderedHeight = h;
      const bounds = orientationBounds.get(orientation),
        aspect = w / h,
        height =
          Math.max(
            bounds.max.y - bounds.min.y,
            (bounds.max.x - bounds.min.x) / aspect,
          ) * (options.compact ? 1.24 : 1.27),
        cx = (bounds.min.x + bounds.max.x) / 2,
        cy = (bounds.min.y + bounds.max.y) / 2;
      camera.left = cx - (height * aspect) / 2;
      camera.right = cx + (height * aspect) / 2;
      camera.top = cy + height / 2;
      camera.bottom = cy - height / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
      if (detailRenderer) detailRenderer.setSize(w, h, false);
      if (inspection && inspection.phase !== "in") {
        const goal = inspectionGoal();
        inspection.toPosition = goal.position;
        inspection.toScale = goal.scale;
        if (inspection.phase === "ready") {
          inspection.group.position.copy(goal.position);
          inspection.group.scale.setScalar(goal.scale);
        }
      }
      wake();
    }
    mobileView?.addEventListener("change", resize, events);
    const ro = new ResizeObserver(resize);
    ro.observe(stage);
    const io = new IntersectionObserver(
      (entries) => {
        inViewport = entries[0].isIntersecting;
        active = inViewport && !document.hidden;
        if (active) {
          resize();
          wake();
        } else if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
          last = 0;
        }
      },
      { threshold: 0 },
    );
    io.observe(stage);
    document.addEventListener(
      "visibilitychange",
      () => {
        active = inViewport && !document.hidden;
        if (active) wake();
        else {
          cancelAnimationFrame(raf);
          raf = 0;
          last = 0;
        }
      },
      events,
    );
    function trackball(x, y) {
      const r = stage.getBoundingClientRect(),
        scale = Math.min(r.width, r.height) * 0.44,
        v = new T.Vector3(
          (x - r.left - r.width / 2) / scale,
          (r.height / 2 - y + r.top) / scale,
          0,
        ),
        d = v.lengthSq();
      if (d <= 1) v.z = Math.sqrt(1 - d);
      else v.normalize();
      return v.applyQuaternion(camera.quaternion);
    }
    const raycaster = new T.Raycaster();
    function hit(e, object = box) {
      const r = stage.getBoundingClientRect();
      raycaster.setFromCamera(
        new T.Vector2(
          ((e.clientX - r.left) / r.width) * 2 - 1,
          (-(e.clientY - r.top) / r.height) * 2 + 1,
        ),
        camera,
      );
      const hits = raycaster.intersectObject(object, true).filter((h) => {
        for (let o = h.object; o; o = o.parent) if (!o.visible) return false;
        return true;
      });
      if (!hits.length) return null;
      let o = hits[0].object;
      while (o && !o.userData.action) o = o.parent;
      return o;
    }
    stage.addEventListener(
      "pointerdown",
      (e) => {
        if (!interactive || e.button !== 0 || e.target.closest("button"))
          return;
        if (inspection && inspection.phase !== "ready") return;
        drag = {
          x: e.clientX,
          y: e.clientY,
          vector: trackball(e.clientX, e.clientY),
          q: (inspection?.rotationTarget || rotationTarget).clone(),
          moved: false,
        };
        stage.setPointerCapture(e.pointerId);
      },
      events,
    );
    stage.addEventListener(
      "pointermove",
      (e) => {
        if (!interactive) return;
        if (drag) {
          if (Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 5)
            drag.moved = true;
          if (drag.moved) {
            (inspection?.rotationTarget || rotationTarget)
              .copy(
                new T.Quaternion().setFromUnitVectors(
                  drag.vector,
                  trackball(e.clientX, e.clientY),
                ),
              )
              .multiply(drag.q);
            wake();
          }
        } else if (!inspection) {
          stage.style.cursor = hit(e) ? "pointer" : "grab";
        }
      },
      events,
    );
    stage.addEventListener(
      "pointerup",
      (e) => {
        if (!interactive) return;
        if (drag && !drag.moved && inspection?.phase === "ready") {
          if (!hit(e, inspection.group)) closeInspection();
        } else if (drag && !drag.moved && !inspection) {
          const o = hit(e);
          if (o?.userData.action === "lid") toggle();
          else if (o) inspect(o.userData.action, o.userData.item, o);
        }
        drag = null;
      },
      events,
    );
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (inspection && event.button === 0 && !stage.contains(event.target))
          closeInspection(false, false);
      },
      events,
    );
    stage.addEventListener("pointercancel", () => (drag = null), events);
    stage.addEventListener(
      "keydown",
      (e) => {
        if (!interactive) return;
        if (inspection && e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          closeInspection();
          return;
        }
        if (e.target !== stage || (inspection && inspection.phase !== "ready"))
          return;
        const directions = {
          ArrowLeft: [0, 1, 0, -0.18],
          ArrowRight: [0, 1, 0, 0.18],
          ArrowUp: [1, 0, 0, -0.18],
          ArrowDown: [1, 0, 0, 0.18],
        };
        if (directions[e.key]) {
          e.preventDefault();
          const [x, y, z, a] = directions[e.key];
          (inspection?.rotationTarget || rotationTarget).premultiply(
            new T.Quaternion().setFromAxisAngle(
              new T.Vector3(x, y, z).applyQuaternion(camera.quaternion),
              a,
            ),
          );
          wake();
        }
        if (e.key === "Home") {
          e.preventDefault();
          (inspection?.rotationTarget || rotationTarget).copy(
            inspection?.upright || homeQuaternion,
          );
          wake();
        }
      },
      events,
    );
    function setItems(items, animate = true) {
      items = items.slice(0, 5);
      const key = items.map((p) => (p ? String(p.id) : "")).join(",");
      if (key === itemsKey) return;
      closeInspection(true, false);
      itemsKey = key;
      const ids = new Set(items.filter(Boolean).map((p) => String(p.id))),
        instant = !animate || reduced.matches;
      for (const s of samples.values())
        if (!ids.has(s.id) && s.target) {
          if (instant) remove(s);
          else flight(s, false);
        }
      items.forEach((p, i) => {
        if (!p) return;
        const id = String(p.id);
        let s = samples.get(id),
          isNew = !s;
        if (!s) {
          s = makeSample(id, i);
          samples.set(id, s);
        }
        const changed = isNew || !s.target || s.index !== i;
        s.index = i;
        s.item = p;
        s.outer.userData.item = p;
        if (instant) {
          s.target = true;
          settle(s);
        } else if (changed) flight(s, true, isNew);
      });
      if (animate) desiredOpen = true;
      stage.dataset.selectedIds = key;
      stage.setAttribute(
        "aria-label",
        interactive
          ? `3D samplebox met ${ids.size} van vijf geuren. Sleep in elke richting om te draaien. Klik op het deksel, een sample of de geurstrips. Met het toetsenbord: pijltjestoetsen om te draaien, Home om te herstellen.`
          : `Ornata samplebox met ${ids.size} samples.`,
      );
      publishState();
      wake();
    }
    resize();
    setItems(options.items || [], false);
    return {
      setItems,
      toggle,
      closeInspection,
      setOpen(value) {
        closeInspection(true, false);
        desiredOpen = !!value;
        wake();
      },
      resize,
      dispose() {
        if (disposed) return;
        disposed = true;
        finishInspection(false);
        detailRenderer?.dispose();
        detailRenderer?.forceContextLoss();
        cancelAnimationFrame(raf);
        abort.abort();
        ro.disconnect();
        io.disconnect();
        const geometries = new Set(),
          materials = new Set([glass, liquid, capMat, tubeMat]),
          textures = new Set([bump, logoTexture]);
        scene.traverse((m) => {
          if (m.geometry) geometries.add(m.geometry);
          for (const mat of [m.material].flat().filter(Boolean)) {
            materials.add(mat);
            if (mat.map) textures.add(mat.map);
          }
        });
        geometries.forEach((geometry) => geometry.dispose());
        materials.forEach((material) => material.dispose());
        textures.forEach((texture) => texture.dispose());
        key.shadow.dispose();
        envTarget.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        stage.replaceChildren();
      },
    };
  } catch (error) {
    if (options.signal?.aborted)
      return { setItems() {}, setOpen() {}, resize() {}, dispose() {} };
    console.warn("3D-weergave niet beschikbaar:", error.message);
    stage.dataset.modelState = "fallback";
    stage.setAttribute("aria-busy", "false");
    if (fallback) stage.replaceChildren(fallback);
    else {
      stage.replaceChildren();
      const message = document.createElement("span");
      message.className = "ornata-model-error";
      message.textContent = "3D-weergave niet beschikbaar";
      stage.append(message);
    }
    return {
      setItems() {},
      toggle() {
        return true;
      },
      setOpen() {},
      resize() {},
      dispose() {},
    };
  }
};
